/**
 * O contrato da Ordem de Serviço da jornada (F13, D-21), do lado da tela.
 *
 * Espelha as respostas de `bff/v1/ordens-servico` (API, módulo
 * `ordens-servico`; banco, migração 0254). A OS liga 1 veículo + 1 condutor +
 * 1 monitor + 1 DRE do credenciado, exige Termo de Adesão ativo e nasce
 * EMITIDA; só ATIVA e dentro da vigência autoriza a operação (edital 4.1.7).
 *
 * `StatusOrdem`, `ROTULO_DO_STATUS`, `VARIANTE_DO_STATUS`, `numeroExibido` e
 * `vigenciaLegivel` também servem à aba "OS vinculadas" do Termo
 * (`operacional/termos`) — a assinatura deles não muda.
 */

import type { StatusVariant } from '@/ui/StatusBadge';

/** Espelha o enum `status_ordem` do banco (ATIVA desde a 0075). */
export const STATUS_ORDEM = [
  'RASCUNHO',
  'EMITIDA',
  'ATIVA',
  'EM_EXECUCAO',
  'CONCLUIDA',
  'CANCELADA',
] as const;
export type StatusOrdem = (typeof STATUS_ORDEM)[number];

export const ROTULO_DO_STATUS: Record<StatusOrdem, string> = {
  RASCUNHO: 'Rascunho',
  EMITIDA: 'Emitida',
  ATIVA: 'Ativa',
  EM_EXECUCAO: 'Em execução',
  CONCLUIDA: 'Concluída',
  CANCELADA: 'Cancelada',
};

/** Tom já aprovado no `StatusBadge`; nenhuma cor nova. */
export const VARIANTE_DO_STATUS: Record<StatusOrdem, StatusVariant> = {
  RASCUNHO: 'rascunho',
  EMITIDA: 'emitida',
  ATIVA: 'ativo',
  EM_EXECUCAO: 'em_execucao',
  CONCLUIDA: 'concluida',
  CANCELADA: 'cancelado',
};

/** Os estados que a OS da jornada percorre, para o filtro da lista. */
export const STATUS_DA_JORNADA = ['EMITIDA', 'ATIVA', 'CANCELADA'] as const;

export const SITUACOES_DE_VIGENCIA = ['A_INICIAR', 'VIGENTE', 'ENCERRADA'] as const;
export type SituacaoDeVigencia = (typeof SITUACOES_DE_VIGENCIA)[number];

export const ROTULO_DA_SITUACAO: Record<SituacaoDeVigencia, string> = {
  A_INICIAR: 'Ativa, a iniciar',
  VIGENTE: 'Vigente',
  ENCERRADA: 'Vigência encerrada',
};

/** Modalidades do edital (D-15), enum `tipo_veiculo_bancada`. */
export const MODALIDADES = ['CONVENCIONAL', 'ACESSIVEL', 'ESPECIAL', 'CRECHE'] as const;
export type Modalidade = (typeof MODALIDADES)[number];

export const ROTULO_DA_MODALIDADE: Record<Modalidade, string> = {
  CONVENCIONAL: 'TEG comum',
  ACESSIVEL: 'Acessível',
  ESPECIAL: 'Especial',
  CRECHE: 'Creche',
};

export function rotuloDaModalidade(m: string | null | undefined): string {
  if (!m) return '—';
  return ROTULO_DA_MODALIDADE[m as Modalidade] ?? m;
}

export type TipoDeRecurso = 'VEICULO' | 'CONDUTOR' | 'MONITOR';

export const ROTULO_DO_RECURSO: Record<TipoDeRecurso, string> = {
  VEICULO: 'Veículo',
  CONDUTOR: 'Condutor',
  MONITOR: 'Monitor',
};

export interface DreResumo {
  id: string;
  codigo: string;
  nome: string;
}

export interface CredenciadoResumo {
  id: string;
  nome: string;
  tipo: 'PF' | 'PJ' | 'COOPERATIVA' | null;
  /** CPF ou CNPJ, só dígitos. */
  documento: string | null;
}

/** Uma linha da grade (os_linha). De condutor e monitor, só o nome. */
export interface LinhaOs {
  id: string;
  numero: number;
  numeroCompleto: string | null;
  status: StatusOrdem;
  situacaoVigencia: SituacaoDeVigencia | null;
  inicio: string;
  fim: string;
  modalidade: Modalidade;
  credenciado: CredenciadoResumo;
  dre: DreResumo;
  placa: string | null;
  crm: string | null;
  condutorNome: string | null;
  monitorNome: string | null;
  emitidaEm: string;
  ativadaEm: string | null;
}

export interface PaginaDeOs {
  itens: LinhaOs[];
  total: number;
  pagina: number;
  tamanho: number;
}

export interface EscolaDaOs {
  codigoEol: string;
  nome: string;
  prioridade?: number;
}

export type AcaoDaOs = 'ATIVAR' | 'CANCELAR';

export interface EventoDaOs {
  tipo: 'EMITIDA' | 'ATIVADA' | 'CANCELADA';
  em: string;
  autor: string;
  emailEnfileirado: boolean | null;
  motivo: string | null;
}

export interface DetalheOs extends LinhaOs {
  observacoes: string | null;
  termo: { id: string; numero: string | null; inicio: string; fim: string };
  edital: string;
  escolas: EscolaDaOs[];
  veiculo: {
    id: string;
    crm: string | null;
    placa: string | null;
    marca: string | null;
    modelo: string | null;
    anoFabricacao: number | null;
    anoModelo: number | null;
    tipo: string | null;
    capacidade: number | null;
  };
  /** CPF só dígitos. */
  condutor: { id: string; nome: string | null; crmc: string | null; cpf: string | null };
  /** CPF só dígitos. */
  monitor: { id: string; nome: string | null; cpf: string | null };
  inscricaoProtocolo: string | null;
  documentoSha256: string;
  reservas: { tipo: TipoDeRecurso; status: 'ATIVA' | 'LIBERADA'; inicio: string; fim: string }[];
  cancelamento: { em: string; motivo: string } | null;
  eventos: EventoDaOs[];
  acoesPermitidas: AcaoDaOs[];
}

/** O Anexo VIII inteiro (os_documento), com CPF: só sai com finalidade registrada. */
export interface DocumentoOs {
  modelo: 'ANEXO_VIII';
  numeroOs: string;
  numero: number;
  emitidaEm: string;
  edital: string;
  status: StatusOrdem;
  sha256: string;
  termo: { numero: string | null; inicio: string; fim: string };
  credenciado: { nome: string; tipo: string | null; documento: string | null };
  dre: { codigo: string; nome: string };
  modalidade: Modalidade;
  vigencia: { inicio: string; fim: string };
  veiculo: DetalheOs['veiculo'];
  condutor: { nome: string | null; crmc: string | null; cpf: string | null };
  monitor: { nome: string | null; cpf: string | null };
  escolas: EscolaDaOs[];
  inscricao: { protocolo: string } | null;
}

export interface OpcoesDaOs {
  dres: DreResumo[];
  modalidades: Modalidade[];
  podeEmitir: boolean;
  /** Data civil de São Paulo, AAAA-MM-DD: o dia em que a aptidão é conferida. */
  hoje: string;
}

export type MotivoIndisponivel =
  'PERIODO_INVALIDO' | 'INAPTO' | 'APTIDAO_NAO_COBRE_PERIODO' | 'RESERVADO_NO_PERIODO';

/**
 * `APTIDAO_NAO_COBRE_PERIODO` é o código que a API devolve para a aptidão que
 * não vale hoje: a OS confere a aptidão na data da emissão, não na vigência
 * inteira (0273). O código fala em período; o rótulo diz o que a regra é.
 */
export const ROTULO_DO_MOTIVO: Record<MotivoIndisponivel, string> = {
  PERIODO_INVALIDO: 'informe a vigência',
  INAPTO: 'inapto',
  APTIDAO_NAO_COBRE_PERIODO: 'sem aptidão válida hoje',
  RESERVADO_NO_PERIODO: 'já está em outra OS no período',
};

export interface RecursoDoCredenciado {
  tipo: TipoDeRecurso;
  id: string;
  identificacao: string | null;
  detalhe: string | null;
  modalidade: Modalidade | null;
  aptidao: { status: 'APTO' | 'INAPTO'; inicio: string | null; validadeLimite: string | null };
  disponivel: boolean;
  motivo: MotivoIndisponivel | null;
}

export interface OpcoesDoCredenciado {
  credenciado: CredenciadoResumo & { status: string };
  termo: { id: string; numero: string | null; inicio: string; fim: string } | null;
  recursos: RecursoDoCredenciado[];
  sugestao: {
    modalidade: Modalidade | null;
    dreId: string | null;
    protocolo: string | null;
    escolas: { codigoEol: string; nome: string; prioridade?: number }[];
  } | null;
}

export interface EscolaDoEol {
  codigoEol: string;
  nome: string;
  codigoDre: string;
}

export interface PedidoDeEmissao {
  credenciadoId: string;
  termoId: string;
  veiculoId: string;
  condutorId: string;
  monitorId: string;
  dreId: string;
  modalidade: Modalidade;
  escolasCodigosEol: string[];
  inicio: string;
  fim: string;
  observacoes?: string;
  chaveIdempotencia: string;
}

// ---------------------------------------------------------------------------
// Formatação
// ---------------------------------------------------------------------------

/** Número da OS como a SMT lê (2026-0000001-000). */
export function numeroExibido(ordem: { numero: number; numero_completo: string | null }): string {
  return ordem.numero_completo ?? `OS-${String(ordem.numero).padStart(4, '0')}`;
}

export function numeroDaOs(o: { numero: number; numeroCompleto: string | null }): string {
  return numeroExibido({ numero: o.numero, numero_completo: o.numeroCompleto });
}

/** Data em português, sem hora — as datas de OS são `date`. */
export function dataLegivel(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [ano, mes, dia] = iso.slice(0, 10).split('-');
  if (!ano || !mes || !dia) return iso;
  return `${dia}/${mes}/${ano}`;
}

export function vigenciaLegivel(dataInicio: string | null, dataFim: string | null): string {
  if (!dataInicio && !dataFim) return '—';
  return `${dataLegivel(dataInicio)} a ${dataLegivel(dataFim)}`;
}

/** Data e hora no fuso de quem lê — trilha e ativação são `timestamptz`. */
export function dataHoraLegivel(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

/** Soma dias a uma data AAAA-MM-DD, sem fuso (as datas da OS são civis). */
export function somarDias(iso: string, dias: number): string {
  const [a, m, d] = iso.split('-').map(Number);
  const data = new Date(Date.UTC(a!, (m ?? 1) - 1, d ?? 1));
  data.setUTCDate(data.getUTCDate() + dias);
  return data.toISOString().slice(0, 10);
}

export function menorData(a: string, b: string): string {
  return a <= b ? a : b;
}
